/**
 * row.js
 */

import React from 'react';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Types } from 'utils/constants';
import ContextMenu from '../context-menu';
import { shrinkOptionalWidth } from '../helpers';
import RowCheckbox from './row-checkbox';


const useStyles = makeStyles(theme => ({
  row: {
    position: 'relative',
    cursor: 'pointer',
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    '&.header': {
      color: theme.palette.sys.onPrimary,
    },
    '&.content': {
      backgroundColor: theme.palette.sys.surfaceContainer,
      borderTop: `1px solid ${theme.palette.sys.outlineVariant}`,
      transition: `${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut}`,
      '&:hover': {
        backgroundColor: theme.palette.action.hoverBackground(theme.palette.sys.surfaceContainer)
      },
    },
    '& > *': {
      padding: `0 ${theme.spacing(0.75)}`,
    },
    '& .record-link': {
      zIndex: 0
    } ,
    '& .ellipsis-overflow': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1),
    }
  },
  noRouteLink: {
    cursor: 'default',
  },
  checkboxCol: {
    padding: 0,
    marginLeft: -6,
    marginRight: 2,
    width: theme.spacing(4.5),
    [theme.breakpoints.down('md')]: {
      display: 'none',
    }
  },
  textCol: {
    whiteSpace: 'nowrap',
    lineHeight: 1.4,
  },
  titleCol: {
    flexBasis: '25%',
    [theme.breakpoints.down('sm')]: {
      display: props => props.isHeader ? 'none' : 'block',
      flex: 1,
    }
  },
  detailCol: {
    flex: 1,
    overflow: 'hidden',
    '& .location-date': {
      display: 'flex',
      overflow: 'hidden',
    },
    [theme.breakpoints.down('sm')]: {
      display: props => props.isHeader ? 'flex' : 'none'
    }
  },
  optionalCol: {
    flexBasis: '25%',
    '& .record-status': {
      flex: 1,
      justifyContent: 'flex-end',
      // desktop-only
      '& .status-desktop': {
        [theme.breakpoints.down(shrinkOptionalWidth)]: {
          display: 'none'
        }
      },
      // mobile-only
      '& .status-mobile': {
        display: 'none',
        [theme.breakpoints.down(shrinkOptionalWidth)]: {
          display: 'flex',
          alignItems: 'center'
        }
      }
    },
    '& .review-status': {
      display: 'flex',
      marginRight: theme.spacing(1),
    },
    [theme.breakpoints.down(shrinkOptionalWidth)]: {
      display: 'flex',
      minWidth: theme.spacing(4.5),
    },
    [theme.breakpoints.down('sm')]: {
      flexBasis: 'unset',
      display: props => props.isHeader
        ? 'none'
        : 'flex'
    }
  },
  menuCol: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: theme.spacing(5.25),
    '& .MuiIconButton-root': {
      padding: theme.spacing(1),
      margin: theme.spacing(-1),
      color: 'inherit',
    },
    [theme.breakpoints.down('sm')]: {
      width: 'unset',
      display: props => props.isHeader && props.recordType === Types.Record.DEPLOYMENT
        ? 'none'
        : 'flex'
    }
  }
}));


const Row = React.memo(function (props) {
  const {
    recordType,
    isHeader,
    className,
    routeLink,
    selection,
    icon,
    textVariant,
    title,
    detail,
    optional,
    menu,
    'data-testid': dataTestid,
    'data-testtype': dataTestType
  } = props;
  const classes = useStyles({ recordType, isHeader });
  

  return (
    <>
      <div 
        className={`flex-hc ${classes.row} ${className || ''} ${!routeLink ? classes.noRouteLink : ''}`} 
        data-testid={dataTestid}
        data-testtype={dataTestType}
      >
        {routeLink}

        {/* checkbox */}
        <div className={classes.checkboxCol}>
          {selection &&
          <RowCheckbox
            id={selection.id}
            type={selection.type}
            isSelected={selection.isSelected}
            isDisabled={selection.isDisabled}
            disabledReason={selection.disabledReason}
            onChange={selection.onChange}
          />}
        </div>


        {/* icon */}
        <div className='flex-hc'>
          {icon}
        </div>


        {/* title */}
        <Typography
          variant={textVariant}
          className={`${classes.textCol} ${classes.titleCol} ellipsis-overflow`}
          data-testtype='record-title'
        >
          {title}
        </Typography>


        {/* detail */}
        <div 
          className={`${classes.textCol} ${classes.detailCol} ellipsis-overflow`}
          data-testtype='record-detail'
        >
          {detail}
        </div>


        {/* record status */}
        <div className={classes.optionalCol}>
          {optional}
        </div>


        {/* menu */}
        {menu &&
        <div className={classes.menuCol}>
          {menu &&
          <ContextMenu
            size='small'
            aria-label={menu.ariaLabel}
            menuItems={menu.items}
            className={menu.className}
            data-testtype="row-context-menu"
          />}
        </div>}

      </div>
    </>
  )
}, (prev, next) => {
  return (
    prev.id === next.id
    && prev.selection?.isSelected === next.selection?.isSelected
    && prev.selection?.isDisabled === next.selection?.isDisabled
    && prev.title === next.title
    && prev.detail === next.detail
    && prev.customCompare === next.customCompare
  );
});

export default Row;